import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getCaseModalities, getWaveformChecks, getWaveformQa, saveWaveformQa,
         addTechFlag, resolveTechFlag, clearDocumentsCache } from "../../lib/api";
import { ScoreRing } from "../SharedComponents";
import { useMsal } from "@azure/msal-react";
import { useRoles } from "../../lib/useRoles";

// Waveform flags ride on the same tech-flag records as the documentation
// checks, under the case's PID_ document. The prefix keeps a waveform check id
// from colliding with a documentation check id; the backend strips it again
// when it returns flags keyed by check id.
const WF_FLAG_PREFIX = "WF_";

/* ═══════════════════════════════════════════════════════════════════
   MANUAL WAVEFORM QA TAB

   Backend returns a FLAT check list plus criteria_order / modality_order.
   Grouping happens here: CriteriaValue -> Modality -> SubCriteriaValue.

   Modality is *scope*, not display: the pill row decides which checks are in
   scope for scoring, independent of how they're grouped. "General" is always
   in scope (baseline/event checks apply to every case).
   ═══════════════════════════════════════════════════════════════════ */

const ANSWER_STYLE = {
  yes: { label: "Yes",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  no:  { label: "No",   color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  na:  { label: "N/A",  color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
};

const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }}>
    <path d="M4 6l4 4 4-4"/>
  </svg>
);

const WaveIcon = ({ color = "#0d9488", size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polyline points="2 12 6 6 10 18 14 8 18 14 22 12"/>
  </svg>
);

/* ── Source badges ───────────────────────────────────────────────────────────
   Known tokens get a brand code + color; unknown tokens get a code derived from
   the string and a stable hashed color, so new sources need no code change.
   Grey-out applies only to file-backed sources; system sources (Database) are
   always shown as present. */
const SOURCE_CODES = {
  "usmon chat log":        { code: "CHAT", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", isFile: true,  fileKey: "Usmon Chat Log" },
  "chat log":              { code: "CHAT", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", isFile: true,  fileKey: "Usmon Chat Log" },
  "screenshots":           { code: "SCRN", color: "#d97706", bg: "#fffbeb", border: "#fde68a", isFile: true,  fileKey: "Usmon Screenshots" },
  "usmon screenshots":     { code: "SCRN", color: "#d97706", bg: "#fffbeb", border: "#fde68a", isFile: true,  fileKey: "Usmon Screenshots" },
  "interpretation report": { code: "IP",   color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe", isFile: true,  fileKey: "Interpretation Report" },
  "technical report":      { code: "TECH", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc", isFile: true,  fileKey: "Technical Report" },
  "database":              { code: "DB",   color: "#475569", bg: "#f8fafc", border: "#e2e8f0", isFile: false },
};

const FALLBACK_PALETTE = [
  { color: "#0f766e", bg: "#f0fdfa", border: "#ccfbf1" },
  { color: "#9333ea", bg: "#faf5ff", border: "#e9d5ff" },
  { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  { color: "#4338ca", bg: "#eef2ff", border: "#c7d2fe" },
  { color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
];

function hashIdx(str, n) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) % n;
}

function shortCode(token) {
  const words = token.trim().split(/\s+/).filter(Boolean);
  if (words.length > 1) return words.map(w => w[0]).join("").slice(0, 4).toUpperCase();
  return token.trim().slice(0, 4).toUpperCase();
}

function resolveSource(token) {
  const known = SOURCE_CODES[token.trim().toLowerCase()];
  if (known) return { ...known, label: known.code };
  const pal = FALLBACK_PALETTE[hashIdx(token.trim().toLowerCase(), FALLBACK_PALETTE.length)];
  return { code: shortCode(token), label: shortCode(token), isFile: false, ...pal };
}

function SourceBadges({ sources, availableSources }) {
  if (!sources) return null;
  const parts = sources.split(/[,/]| and /).map(s => s.trim()).filter(Boolean);
  if (!parts.length) return null;
  const knowAvail = availableSources && availableSources.size > 0;
  return (
    <span style={{ display: "inline-flex", gap: 3, flexWrap: "wrap", verticalAlign: "middle" }}>
      {parts.map((raw, i) => {
        const s = resolveSource(raw);
        let { bg, color, border } = s, suffix = null, title = raw;
        if (s.isFile && knowAvail) {
          if (availableSources.has(s.fileKey)) { title = `${raw} — uploaded`; }
          else { bg = "#f8fafc"; color = "#94a3b8"; border = "#e2e8f0"; suffix = "✗"; title = `${raw} — not uploaded`; }
        }
        return (
          <span key={i} title={title} style={{
            fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "1px 5px", borderRadius: 3,
            background: bg, color, border: `1px solid ${border}`, letterSpacing: "0.03em",
            display: "inline-flex", alignItems: "center", gap: 2 }}>
            {suffix && <span style={{ fontSize: 8, opacity: 0.85 }}>{suffix}</span>}
            {s.label}
          </span>
        );
      })}
    </span>
  );
}

/* ── Grouping: Criteria -> Modality -> SubCriteria ────────────────────────── */
function buildTree(structure, presentSet) {
  if (!structure?.checks) return [];
  const general   = structure.general_modality || "General";
  const critOrder = structure.criteria_order || [];
  const modOrder  = structure.modality_order || [];

  const byCrit = new Map();
  for (const c of structure.checks) {
    if (c.modality !== general && !presentSet.has(c.modality)) continue;  // out of scope
    if (!byCrit.has(c.criteria)) byCrit.set(c.criteria, new Map());
    const byMod = byCrit.get(c.criteria);
    if (!byMod.has(c.modality)) byMod.set(c.modality, new Map());
    const bySub = byMod.get(c.modality);
    const sub = c.subcategory || "";
    if (!bySub.has(sub)) bySub.set(sub, []);
    bySub.get(sub).push(c);
  }

  const sortBy = (arr, order) => arr.sort((a, b) => {
    const ia = order.indexOf(a), ib = order.indexOf(b);
    return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
  });

  return sortBy([...byCrit.keys()], critOrder).map(crit => {
    const byMod = byCrit.get(crit);
    const modalities = sortBy([...byMod.keys()], modOrder).map(mod => {
      const bySub = byMod.get(mod);
      const subs = [...bySub.keys()].sort((a, b) => (a === "" ? -1 : b === "" ? 1 : 0));
      return { code: mod, subcategories: subs.map(name => ({ name, checks: bySub.get(name) })) };
    });
    return { name: crit, modalities };
  });
}

/* ── Score preview — mirrors waveform_qa.score_waveform() exactly ── */
function computeScore(structure, presentSet, answers) {
  let earned = 0, possible = 0, answered = 0, scoredInScope = 0;
  const general = structure?.general_modality || "General";
  for (const c of structure?.checks || []) {
    if (c.type !== "scored") continue;
    if (c.modality !== general && !presentSet.has(c.modality)) continue;
    scoredInScope++;
    const a = answers[c.id];
    if (a === "yes")      { earned += c.max_points; possible += c.max_points; answered++; }
    else if (a === "no")  { possible += c.max_points; answered++; }
    else if (a === "na")  { answered++; }
  }
  return {
    score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
    earned: Math.round(earned * 100) / 100,
    possible: Math.round(possible * 100) / 100,
    scoredInScope,
    answered,
    remaining: Math.max(0, scoredInScope - answered),
  };
}

function critProgress(crit, answers) {
  let scored = 0, answered = 0;
  for (const m of crit.modalities)
    for (const s of m.subcategories)
      for (const c of s.checks) {
        if (c.type !== "scored") continue;
        scored++;
        if (answers[c.id]) answered++;
      }
  return { scored, answered, remaining: scored - answered };
}

/* ── Rows ─────────────────────────────────────────────────────────────────── */
function ScoredCheckRow({ check, value, onChange, locked, availableSources, flagging }) {
  const { flag, isTech, canFlag, onFlag, onResolve } = flagging || {};
  const flagOpen = !!flag && flag.open !== false;
  const [form, setForm]     = useState(null);   // "raise" | "resolve" | null
  const [note, setNote]     = useState("");
  const [busy, setBusy]     = useState(false);
  const [showFlag, setShowFlag] = useState(false);

  const submit = async () => {
    const text = note.trim();
    if (busy || (form === "raise" && !text)) return;
    setBusy(true);
    try {
      if (form === "raise") await onFlag(check.id, text);
      else await onResolve(check.id, text);
      setForm(null); setNote("");
    } catch (e) { alert("Could not save the flag: " + (e?.message || String(e))); }
    setBusy(false);
  };

  return (
    <div style={{ borderBottom: "1px solid var(--border)" }}>
    <div style={{ padding: "9px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.5 }}>{check.question}</span>
        <span style={{ marginLeft: 8, fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>{check.id}</span>
        {check.event_dependent && (
          <span style={{ marginLeft: 6, fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "1px 5px", borderRadius: 3,
            background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" }}>EVENT</span>
        )}
        {check.sources && <span style={{ marginLeft: 6, display: "inline-flex", verticalAlign: "middle" }}><SourceBadges sources={check.sources} availableSources={availableSources}/></span>}
      </div>
      {(flag || canFlag) && (
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {flag && (
            <button onClick={() => setShowFlag(s => !s)} title={flag.note || ""}
              style={{ fontSize: 9.5, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "2px 8px", borderRadius: 4, cursor: "pointer",
                background: flagOpen ? "#fffbeb" : "var(--gray-50, #f9fafb)",
                color:      flagOpen ? "#b45309" : "var(--text-tertiary)",
                border: `1px solid ${flagOpen ? "#fde68a" : "var(--border)"}` }}>
              {flagOpen ? "⚑ Flagged" : "⚑ Resolved"}
            </button>
          )}
          {isTech && canFlag && !flagOpen && (
            <button onClick={() => { setShowFlag(true); setForm("raise"); setNote(""); }}
              style={{ padding: "3px 8px", borderRadius: 5, fontSize: 10, fontFamily: "var(--font-mono)", cursor: "pointer",
                fontWeight: 500, border: "1px solid var(--border)", background: "var(--white)", color: "var(--text-secondary)" }}>
              ⚑ Flag
            </button>
          )}
          {!isTech && flagOpen && (
            <button onClick={() => { setShowFlag(true); setForm("resolve"); setNote(""); }}
              style={{ padding: "3px 8px", borderRadius: 5, fontSize: 10, fontFamily: "var(--font-mono)", cursor: "pointer",
                fontWeight: 500, border: "1px solid #fde68a", background: "#fffbeb", color: "#b45309" }}>
              Resolve
            </button>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        {["yes", "no", "na"].map(k => {
          const s = ANSWER_STYLE[k];
          const active = value === k;
          return (
            <button key={k} disabled={locked}
              onClick={() => onChange(active ? null : k)}
              style={{
                padding: "3px 11px", borderRadius: 5, fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 500,
                cursor: locked ? "default" : "pointer",
                border: `1px solid ${active ? s.border : "var(--border)"}`,
                background: active ? s.bg : "var(--white)",
                color: active ? s.color : "var(--text-tertiary)",
                opacity: locked && !active ? 0.5 : 1,
              }}>
              {s.label}
            </button>
          );
        })}
      </div>
    </div>

    {/* What the tech asked, what came back, and the form to ask or answer */}
    {(showFlag || form) && (flag || form) && (
      <div style={{ padding: "0 16px 10px 16px" }}>
        {flag && (
          <div style={{ marginBottom: form ? 8 : 0, padding: "8px 12px", borderRadius: 6,
            border: `1px solid ${flagOpen ? "#fde68a" : "var(--border)"}`,
            background: flagOpen ? "#fffbeb" : "var(--gray-50, #f9fafb)" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.07em",
              color: flagOpen ? "#b45309" : "var(--text-tertiary)", marginBottom: 3 }}>
              {flagOpen ? "Flagged for a reviewer" : "Flag resolved"}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>{flag.note}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", marginTop: 3 }}>
              {flag.by || "Unknown"}{flag.at ? ` · ${new Date(flag.at).toLocaleString()}` : ""}
            </div>
            {!flagOpen && (flag.resolutionNote || flag.resolvedBy) && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--border)" }}>
                <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>{flag.resolutionNote || "No note left."}</div>
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-tertiary)", marginTop: 3 }}>
                  {isTech ? "Reviewer" : (flag.resolvedBy || "Unknown")}{flag.resolvedAt ? ` · ${new Date(flag.resolvedAt).toLocaleString()}` : ""}
                </div>
              </div>
            )}
          </div>
        )}
        {form && (
          <div style={{ padding: 12, border: "1px solid #fde68a", borderRadius: 8, background: "var(--white)" }}>
            <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 6 }}>
              {form === "raise"
                ? "What should the reviewer look at again? This doesn't change the answer or the score."
                : "Reply to the technologist. To change the answer, reopen the waveform record, update it and sign off again."}
            </div>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              placeholder={form === "raise" ? "Explain why this answer should be reviewed…" : "Your reply (optional)…"}
              style={{ width: "100%", fontSize: 12, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, boxSizing: "border-box", resize: "vertical" }}/>
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 6 }}>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { setForm(null); setNote(""); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={busy || (form === "raise" && !note.trim())} onClick={submit}>
                {busy ? "Saving…" : form === "raise" ? "Send flag" : "Resolve flag"}
              </button>
            </div>
          </div>
        )}
      </div>
    )}
    </div>
  );
}

function ValueCheckRow({ check, value, onChange, locked, availableSources }) {
  return (
    <div style={{ padding: "9px 16px", borderBottom: "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.5 }}>{check.question}</span>
        <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)" }}>{check.id}</span>
        <span style={{ fontSize: 9, fontFamily: "var(--font-mono)", fontWeight: 600, padding: "1px 5px", borderRadius: 3,
          background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }}>VALUE</span>
        {check.sources && <SourceBadges sources={check.sources} availableSources={availableSources}/>}
      </div>
      <input
        value={value || ""}
        disabled={locked}
        onChange={e => onChange(e.target.value)}
        placeholder="Record value…"
        style={{ width: "100%", fontSize: 12, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6,
          background: locked ? "var(--gray-50, #f9fafb)" : "var(--white)", boxSizing: "border-box" }}
      />
    </div>
  );
}

/* ── Criteria section (top level) ─────────────────────────────────────────── */
function CriteriaSection({ crit, answers, values, onAnswer, onValue, expanded, onToggle, locked, availableSources, generalCode, flagsFor }) {
  const prog = critProgress(crit, answers);
  const complete = prog.scored > 0 && prog.remaining === 0;

  return (
    <div className="card" style={{ marginBottom: 12, overflow: "hidden" }}>
      <div onClick={onToggle} style={{
        padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
        borderLeft: "3px solid #14b8a6", background: expanded ? "var(--white)" : "var(--gray-50, #f9fafb)" }}>
        <ChevronIcon open={expanded}/>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", flex: 1 }}>{crit.name}</span>
        <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", fontWeight: 500,
          color: complete ? "#0f766e" : "var(--text-tertiary)",
          background: complete ? "#f0fdfa" : "var(--gray-50, #f9fafb)",
          border: `1px solid ${complete ? "#ccfbf1" : "var(--border)"}`, borderRadius: 4, padding: "1px 7px" }}>
          {prog.answered}/{prog.scored} answered
        </span>
      </div>

      {expanded && crit.modalities.map(mod => (
        <div key={mod.code}>
          <div style={{ padding: "6px 16px", background: "#f0fdfa", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)",
            display: "flex", alignItems: "center", gap: 6 }}>
            <WaveIcon size={11}/>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#0f766e", letterSpacing: "0.02em" }}>
              {mod.code}{mod.code === generalCode ? " — applies to every case" : ""}
            </span>
          </div>
          {mod.subcategories.map((sub, si) => (
            <div key={si}>
              {sub.name && (
                <div style={{ padding: "7px 16px 7px 28px", background: "var(--gray-50, #f9fafb)", borderBottom: "1px solid var(--border)",
                  fontFamily: "var(--font-mono)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.06em",
                  textTransform: "uppercase", color: "var(--text-tertiary)" }}>
                  {sub.name}
                </div>
              )}
              {sub.checks.map(c =>
                c.type === "scored"
                  ? <ScoredCheckRow key={c.id} check={c} value={answers[c.id]} onChange={v => onAnswer(c.id, v)} locked={locked} availableSources={availableSources} flagging={flagsFor?.(c.id)}/>
                  : <ValueCheckRow  key={c.id} check={c} value={values[c.id]}  onChange={v => onValue(c.id, v)}  locked={locked} availableSources={availableSources}/>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN
   ═══════════════════════════════════════════════════════════════════ */
export default function WaveformQATab({ caseId, availableSources,
                                        caseClosed = false,
                                        canCloseCase = false,
                                        onCloseCase = null }) {
  const { accounts } = useMsal();
  const account = accounts && accounts[0];
  // isTech is the effective role, so an admin viewing as a tech sees the tech's
  // read-only view. The backend records the real signed-in user on a flag.
  const { isTech } = useRoles();
  const [flags, setFlags]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [structure, setStructure] = useState(null);

  const [present, setPresent]   = useState(new Set());
  const [answers, setAnswers]   = useState({});
  const [values, setValues]     = useState({});
  const [comments, setComments] = useState({});
  const [signed, setSigned]     = useState(false);
  const [reviewer, setReviewer] = useState(null);
  const [savedAt, setSavedAt]   = useState(null);
  const [serverScore, setServerScore] = useState(null);

  // Offer to close the whole case in the same action as waveform sign-off.
  // Only when the case is otherwise ready to close (both document tracks
  // signed off, not already closed) - otherwise the box is not shown at all.
  const [closeWithSignoff, setCloseWithSignoff] = useState(true);

  const [expandedMap, setExpandedMap] = useState({});
  const [dirty, setDirty]   = useState(false);
  const [saving, setSaving] = useState(false);

  const generalCode = structure?.general_modality || "General";
  const [suggested, setSuggested] = useState(null);
  const [autoFilled, setAutoFilled] = useState(false);

  // A chip label and a USMON modality can be written differently
  // ("UPPER_SSEP" / "Upper SSEP"), so compare on alphanumerics only.
  const normKey = (v) => String(v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

  const load = useCallback(() => {
    setLoading(true); setError(null);
    return Promise.all([
        getWaveformChecks(),
        getWaveformQa(caseId),
        getCaseModalities(caseId).catch(() => null),
      ])
      .then(([struct, rec, usmon]) => {
        setStructure(struct);

        const fromUsmon = usmon?.modalities || [];
        setSuggested(fromUsmon.length ? fromUsmon : null);

        const saved = rec?.present_modalities || [];
        if (saved.length > 0) {
          setPresent(new Set(saved));
          setAutoFilled(false);
        } else if (fromUsmon.length > 0) {
          // Nothing saved yet: start from what USMON records for this case.
          // Chips stay editable - USMON is the billing record, not proof of
          // what was monitored, so a reviewer can still add or remove.
          const wanted = new Set();
          fromUsmon.forEach(m => {
            [m.code, m.display, m.source_label, m.nickname].forEach(v => {
              if (v) wanted.add(normKey(v));
            });
          });
          const picked = (struct?.modality_order || [])
            .filter(label => label !== (struct?.general_modality || "General"))
            .filter(label => wanted.has(normKey(label)));
          setPresent(new Set(picked));
          setAutoFilled(picked.length > 0);
          if (picked.length > 0) setDirty(true);
        } else {
          setPresent(new Set());
          setAutoFilled(false);
        }
        setAnswers(rec?.answers || {});
        setValues(rec?.values || {});
        setComments(rec?.comments || {});
        setSigned(!!rec?.signed);
        setReviewer(rec?.reviewer || null);
        setSavedAt(rec?.updated_at || null);
        setServerScore(rec?.score ?? null);
        setFlags(rec?.flags || {});
        const em = {};
        (struct?.criteria_order || []).forEach((c, i) => { em[c] = i === 0; });
        setExpandedMap(em);
      })
      .catch(e => setError(e?.message || String(e)))
      .finally(() => setLoading(false));
  }, [caseId]);

  useEffect(() => { if (caseId) load(); }, [caseId, load]);

  // A closed case is locked end to end: the waveform record cannot be edited or
  // reopened until the case itself is reopened from the header.
  // A technologist reads the review; they never answer it.
  const locked = signed || caseClosed || isTech;

  const flagDocId = "PID_" + caseId;
  const flagBy = account?.idTokenClaims?.name || account?.name || account?.username || "";
  const raiseFlag = useCallback(async (checkId, note) => {
    await addTechFlag(flagDocId, WF_FLAG_PREFIX + checkId, note, flagBy);
    setFlags(p => ({ ...p, [checkId]: { note, by: flagBy, at: new Date().toISOString(), open: true } }));
    clearDocumentsCache();
  }, [flagDocId, flagBy]);
  const resolveFlag = useCallback(async (checkId, note) => {
    const reply = note || "Answer unchanged";
    await resolveTechFlag(flagDocId, WF_FLAG_PREFIX + checkId, reply, flagBy);
    setFlags(p => ({ ...p, [checkId]: { ...(p[checkId] || {}), open: false, resolvedBy: flagBy,
                                        resolvedAt: new Date().toISOString(), resolutionNote: reply } }));
    clearDocumentsCache();
  }, [flagDocId, flagBy]);
  // Techs flag a finished review only: an unsigned one can still change.
  const flagsFor = useCallback(checkId => ({
    flag: flags[String(checkId)], isTech, canFlag: isTech && signed,
    onFlag: raiseFlag, onResolve: resolveFlag,
  }), [flags, isTech, signed, raiseFlag, resolveFlag]);
  const openFlagCount = Object.values(flags).filter(f => f && f.open !== false).length;

  const toggleModality = useCallback((code) => {
    if (locked) return;
    setPresent(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
    setDirty(true);
  }, [locked]);

  const setAnswer    = useCallback((id, v) => { setAnswers(p => ({ ...p, [id]: v })); setDirty(true); }, []);
  const setValue     = useCallback((id, v) => { setValues(p => ({ ...p, [id]: v })); setDirty(true); }, []);
  const toggleExpand = useCallback((name) => setExpandedMap(p => ({ ...p, [name]: !p[name] })), []);

  const live = useMemo(() => computeScore(structure, present, answers), [structure, present, answers]);
  const tree = useMemo(() => buildTree(structure, present), [structure, present]);

  const selectable = useMemo(
    () => (structure?.modality_order || []).filter(m => m !== generalCode),
    [structure, generalCode]
  );

  const doSave = async (withSignoff) => {
    if (saving) return;
    const alsoClose = withSignoff === true && canCloseCase && closeWithSignoff && !!onCloseCase;
    if (withSignoff && !window.confirm(alsoClose
      ? "Sign off on this waveform QA and close the case? This locks the record and the case. The case can be reopened later."
      : "Sign off on this waveform QA? This locks the record. You can reopen it later if needed.")) return;
    setSaving(true);
    try {
      const payload = {
        present_modalities: Array.from(present),
        answers, values, comments,
        signed: withSignoff === true ? true : withSignoff === false ? false : signed,
        signed_by: account?.idTokenClaims?.name
          || account?.idTokenClaims?.preferred_username
          || account?.name || account?.username || "",
      };
      const res = await saveWaveformQa(caseId, payload);
      setServerScore(res?.score ?? live.score);
      setReviewer(res?.reviewer || reviewer);
      setSavedAt(res?.updated_at || new Date().toISOString());
      setSigned(!!res?.signed);
      setDirty(false);
      // Close the case only after the waveform record is safely saved, and
      // never let a close failure read as a sign-off failure.
      if (alsoClose && res?.signed) {
        try { await onCloseCase(); }
        catch (e) { alert("Waveform QA was signed off, but closing the case failed: " + (e?.message || String(e))); }
      }
    } catch (e) {
      alert("Couldn't save waveform QA: " + (e?.message || String(e)));
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="card" style={{ padding: 20 }}><div className="skeleton" style={{ height: 160, borderRadius: 8 }}/></div>;
  }
  if (error) {
    return (
      <div className="card">
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Couldn't load waveform checks</div>
          <div style={{ fontSize: 12.5, color: "var(--text-tertiary)", marginBottom: 14 }}>{error}</div>
          <button className="btn btn-secondary btn-sm" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  if (isTech && !signed) {
    return (
      <div className="card" style={{ padding: "28px 20px", textAlign: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6 }}>Waveform review not finished</div>
        <div style={{ fontSize: 12.5, color: "var(--text-tertiary)" }}>
          The waveform review for this case will appear here once a reviewer signs it off.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {openFlagCount > 0 && (
        <div style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid #fde68a", background: "#fffbeb",
          fontSize: 12.5, color: "#92400e" }}>
          ⚑ {openFlagCount} waveform question{openFlagCount !== 1 ? "s" : ""} flagged by the technologist
          {isTech ? " and waiting on a reviewer." : ". Open the flag on each question to read it and reply."}
        </div>
      )}

      {/* ── Summary + score ── */}
      <div className="card">
        <div className="card-header" style={{ borderLeft: "3px solid #14b8a6" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 6, background: "#f0fdfa", border: "1px solid #ccfbf1", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <WaveIcon/>
            </div>
            <div>
              <div className="card-title">Waveform QA</div>
              <div className="card-subtitle">Manual review · {live.answered}/{live.scoredInScope} scored checks answered</div>
            </div>
          </div>
          {signed && (
            <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 600, color: "#0f766e",
              background: "#f0fdfa", border: "1px solid #ccfbf1", padding: "3px 9px", borderRadius: 5 }}>
              ✓ Signed{reviewer && !isTech ? ` by ${reviewer}` : ""}
            </span>
          )}
        </div>
        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 20 }}>
          <ScoreRing score={live.score} size={72}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 4 }}>
              {live.earned} of {live.possible} points
              {live.remaining > 0 && <span style={{ color: "var(--text-tertiary)" }}> · {live.remaining} unanswered</span>}
            </div>
            <div style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: dirty ? "#d97706" : "var(--text-tertiary)" }}>
              {dirty ? "Unsaved changes" : savedAt ? `Saved ${new Date(savedAt).toLocaleString()}${serverScore != null ? ` · ${serverScore}%` : ""}` : "Not yet saved"}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modality selector (scope) ── */}
      <div className="card">
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>Modalities monitored</div>
          <div style={{ fontSize: 11.5, color: "var(--text-tertiary)" }}>
            Select which modalities were run. {generalCode} checks always count.
          </div>
          {autoFilled && (
            <div style={{ fontSize: 11, color: "#0f766e", marginTop: 4 }}>
              Pre-selected from USMON: {suggested.map(m => m.source_label || m.display).join(", ")}
              {" "}— adjust if the case differs.
            </div>
          )}
          {suggested && (() => {
            const chips = new Set((structure?.modality_order || []).map(normKey));
            const missing = suggested.filter(m => !chips.has(normKey(m.code))
                                               && !chips.has(normKey(m.display))
                                               && !chips.has(normKey(m.nickname)));
            if (missing.length === 0) return null;
            return (
              <div style={{ fontSize: 11, color: "#b45309", marginTop: 4 }}>
                USMON also records {missing.map(m => m.display || m.code).join(", ")} —
                no waveform checks are defined for {missing.length === 1 ? "it" : "them"}.
              </div>
            );
          })()}
        </div>
        <div style={{ padding: "12px 16px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {selectable.map(m => {
            const on = present.has(m);
            return (
              <button key={m} onClick={() => toggleModality(m)} disabled={locked}
                style={{
                  padding: "5px 12px", borderRadius: 20, fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: on ? 600 : 400,
                  cursor: locked ? "default" : "pointer",
                  border: `1px solid ${on ? "#14b8a6" : "var(--border)"}`,
                  background: on ? "#f0fdfa" : "var(--white)",
                  color: on ? "#0f766e" : "var(--text-secondary)",
                  opacity: locked && !on ? 0.5 : 1,
                }}>
                {on && "✓ "}{m}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Criteria sections ── */}
      {tree.map(crit => (
        <CriteriaSection key={crit.name} crit={crit} answers={answers} values={values}
          onAnswer={setAnswer} onValue={setValue}
          expanded={!!expandedMap[crit.name]} onToggle={() => toggleExpand(crit.name)}
          locked={locked} availableSources={availableSources} generalCode={generalCode}
          flagsFor={flagsFor}/>
      ))}

      {tree.length === 0 && (
        <div className="card" style={{ padding: "20px 16px", textAlign: "center", fontSize: 12.5, color: "var(--text-tertiary)" }}>
          No checks in scope. Select the modalities monitored above.
        </div>
      )}

      {/* ── Save / sign-off ── (reviewers only) */}
      {!isTech && (
      <div className="card" style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        {signed ? (
          <>
            <span style={{ fontSize: 12, color: "var(--text-secondary)", flex: 1 }}>
              Signed off{reviewer ? ` by ${reviewer}` : ""}{savedAt ? ` · ${new Date(savedAt).toLocaleString()}` : ""}.
              {caseClosed && " The case is closed."}
            </span>
            <button className="btn btn-secondary btn-sm" disabled={saving || caseClosed}
              onClick={() => doSave(false)}
              title={caseClosed ? "This case is closed. Reopen the case from the header first." : "Reopen this waveform record for editing."}
              style={{ color: "#d97706" }}>
              {saving ? "Reopening…" : "🔓 Reopen"}
            </button>
          </>
        ) : caseClosed ? (
          <span style={{ fontSize: 12, color: "var(--text-secondary)", flex: 1 }}>
            This case is closed. Reopen the case from the header to record waveform QA.
          </span>
        ) : (
          <>
            <span style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", flex: 1 }}>
              {live.remaining > 0 ? `${live.remaining} scored check${live.remaining !== 1 ? "s" : ""} still unanswered` : "All scored checks in scope answered"}
            </span>
            {canCloseCase && onCloseCase && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer" }}
                title="Both document audits are signed off, so signing off here can close the case in one step. The case can be reopened later.">
                <input type="checkbox" checked={closeWithSignoff} disabled={saving}
                  onChange={e => setCloseWithSignoff(e.target.checked)} />
                Close the case too
              </label>
            )}
            <button className="btn btn-secondary btn-sm" disabled={saving || !dirty} onClick={() => doSave(undefined)}>
              {saving ? "Saving…" : "Save progress"}
            </button>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => doSave(true)}>
              {saving ? "Signing…" : (canCloseCase && closeWithSignoff && onCloseCase ? "Sign off & close case" : "Sign off")}
            </button>
          </>
        )}
      </div>
      )}
    </div>
  );
}